import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doGetFirmwares, setFirmwares } from '@app/store/slices/dataSlice';
import { notificationController } from '@app/controllers/notificationController';
import { FirmwareModel } from './types';
import { ColumnsType } from 'antd/es/table';
import { Image, PopconfirmProps, Tag } from 'antd';
import { Avatar, Input, Modal, Popconfirm, Space, Typography } from 'antd';
import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { getModeDisplay, getProfilePhoto, secondsToString } from '@app/utils/utils';
import { Button } from '@app/components/common/buttons/Button/Button';
import { Table } from '@app/components/common/Table/Table';
import { PageTitle, Toolbar, ToolbarActions } from '@app/components/common/PageToolbar/PageToolbar';
import { Card as CommonCard } from '@app/components/common/Card/Card';
import { Switch } from '@app/components/common/Switch/Switch';
import { updateFirmware, deleteFirmware } from '@app/api/admin.api';

const FirmwaresPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const firmwares = useAppSelector((state) => state.data.firmwares);
    const [isLoading, setLoading] = useState(false);

    useEffect(() => {
        fetchFirmwares();
    }, []);

    const fetchFirmwares = () => {
        setLoading(true);
        dispatch(doGetFirmwares())
            .unwrap()
            .then((res) => {
                dispatch(setFirmwares(res));
                setLoading(false);
            })
            .catch((err) => {
                notificationController.error({ message: err.message });
                setLoading(false);
            });
    };

    const onIsProductionFileChanged = (isProductionFile: boolean, recordId: number) => {
        const updatedFirmware = { is_production_file: isProductionFile }
        updateFirmware(recordId, updatedFirmware)
            .then(res => {
                console.log('updatedFirmware', res);
                const updatedfirmwares = firmwares.map(data => (data.id === res.id ? res : data));
                dispatch(setFirmwares(updatedfirmwares));
                fetchFirmwares()
                notificationController.success({ message: 'Firmware updated successfully' })

            })
            .catch(error => {
                notificationController.error({ message: error.message });
            })
    }

    const columns: ColumnsType<FirmwareModel> = [
        {
            title: 'File Name',
            dataIndex: 'filename',
        },
        {
            title: 'Version',
            dataIndex: 'version',
        },
        {
            title: 'WiFi OTA (.bin)',
            dataIndex: 'bin_filename',
            render: (text: string, record: FirmwareModel) =>
                record.bin_file ? (
                    <Space direction="vertical" size={0}>
                        <Typography.Text>{record.bin_filename}</Typography.Text>
                        <Typography.Text type="secondary">checksum: {record.bin_checksum}</Typography.Text>
                    </Space>
                ) : (
                    <Tag>Not uploaded</Tag>
                ),
        },
        {
            title: 'Created By',
            dataIndex: 'created_by',
            width: '15%',
            render: (_, { created_by }: FirmwareModel) => (
                <Space size={'small'}>
                    <Avatar src={getProfilePhoto(created_by.profile_picture)} alt="User" shape="circle" size={40} />
                    <Typography.Text>{created_by.name}</Typography.Text>
                </Space>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'is_production_file',
            render: (text: string, firmware: FirmwareModel) => {
                return (
                    <Space>
                        {firmware.is_production_file ? <Tag color="green">Production</Tag> : <Tag color="blue">Testing</Tag>}
                    </Space>
                );
            },
        },
        {
            title: 'Action',
            dataIndex: 'actions',
            render: (text: string, record: FirmwareModel) => {
                return (
                    <Space>
                        <Popconfirm
                            title={`Are you sure you want to set v${record.version} to ${record.is_production_file ? 'testing' : 'production'}?`}
                            onConfirm={() => onIsProductionFileChanged(!record.is_production_file, record.id)}
                            okText="Yes"
                            cancelText="No">
                            <Button size="small" type="primary">
                                {record.is_production_file ? 'Set to Testing' : 'Set to Production'}
                            </Button>
                        </Popconfirm>
                        <a href={record.file} title="Download .hex">
                            <Button size="small" type="primary" icon={<DownloadOutlined />}>
                                hex
                            </Button>
                        </a>
                        {record.bin_file && (
                            <a href={record.bin_file} title="Download .bin">
                                <Button size="small" type="primary" icon={<DownloadOutlined />}>
                                    bin
                                </Button>
                            </a>
                        )}
                        <Popconfirm
                            title={`Are you sure you want to delete firmware v${record.version}?`}
                            onConfirm={() => {
                                deleteFirmware(record.id)
                                    .then((res) => {
                                        console.log('res', res);
                                        notificationController.success({ message: `Firmware v${record.version} deleted successfully.` });
                                        fetchFirmwares()
                                    })
                                    .catch((error) => {
                                        console.log('error: ', error);
                                        notificationController.error({ message: error });
                                    });
                            }}
                            okText="Yes"
                            cancelText="No">
                            <Button danger disabled={record.is_production_file} size="small" type="primary" icon={<DeleteOutlined />}>
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];


    return (
        <>
            <Toolbar>
                <PageTitle>Firmwares</PageTitle>
                <ToolbarActions>
                    <Button
                        type="primary"
                        onClick={() => {
                            navigate('create');
                        }}
                    >
                        {'Create Firmware'}
                    </Button>
                </ToolbarActions>
            </Toolbar>
            <CommonCard id="basic-table" padding="1.25rem 1.25rem 0">
                <Table
                    columns={columns}
                    dataSource={firmwares}
                    loading={isLoading}
                    scroll={{ x: 800 }}
                // onRow={(record, rowIndex) => {
                //   return {
                //     onClick: (event) => {
                //       navigate(`edit`, { state: { record } });
                //     },
                //   };
                // }}
                />
            </CommonCard>
        </>
    );
};

export default FirmwaresPage;
